import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import type { Segment } from '@/components/ui/SegmentedControl'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Skeleton } from '@/components/ui/Skeleton'
import { Toggle } from '@/components/ui/Toggle'
import { useAuth, useUpdateMyPreferences } from '@/lib/auth'
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/lib/notification-api'
import type { NotificationMode } from '@/lib/notification-types'

const MODE_SEGMENTS: Segment<NotificationMode>[] = [
  { value: 'DIRECT', label: 'Direct' },
  { value: 'DIGEST', label: 'Dagelijks' },
  { value: 'OFF', label: 'Uit' },
]

export function PersonalTab() {
  const preferencesQuery = useNotificationPreferences()
  const update = useUpdateNotificationPreferences()
  const preferences = preferencesQuery.data

  return (
    <div className="space-y-6">
      <DisplayCard />

      <Card>
        <CardHeader
          title="Meldingen"
          subtitle="Per soort: meteen een mail, meeliften op de dagelijkse samenvatting, of helemaal niet"
        />
        {preferencesQuery.isError ? (
          <ErrorState compact error={preferencesQuery.error} onRetry={() => preferencesQuery.refetch()} />
        ) : preferencesQuery.isLoading || !preferences ? (
          <Skeleton className="mt-4 h-64 w-full" />
        ) : (
          <div className="mt-4 divide-y divide-hairline">
            {preferences.categories.map((category) => (
              <div key={category.key} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{category.label}</p>
                  <p className="mt-0.5 text-sm text-ink-faint">{category.help}</p>
                </div>
                {category.mandatory ? (
                  <span className="label-caps shrink-0" title="Beveiligingsmeldingen kun je niet uitzetten">
                    Altijd aan
                  </span>
                ) : (
                  <SegmentedControl
                    className="shrink-0"
                    label={`Meldingen over ${category.label}`}
                    segments={MODE_SEGMENTS}
                    value={category.mode}
                    onChange={(mode) => update.mutate({ categories: { [category.key]: mode } })}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Rust" subtitle="Wanneer meldingen je mogen bereiken" />
        {preferencesQuery.isLoading || !preferences ? (
          <Skeleton className="mt-4 h-16 w-full" />
        ) : (
          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">Stil buiten werkdagen</p>
              <p className="mt-0.5 text-sm text-ink-faint">
                Meldingen die in het weekend, op een feestdag, tijdens je vakantie of buiten kantooruren ontstaan,
                wachten tot de eerstvolgende werkdag.
              </p>
            </div>
            <Toggle
              checked={preferences.quietOutsideWorkdays}
              onChange={(value) => update.mutate({ quietOutsideWorkdays: value })}
              label="Stil buiten werkdagen"
            />
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * De weergavevoorkeur staat op /api/me en niet in de meldingen-payload, dus deze
 * kaart heeft zijn eigen mutatie. Een externe boeker ziet hem niet: die komt niet
 * op het dashboard. De API blijft rolloos, alleen het scherm zwijgt.
 */
function DisplayCard() {
  const auth = useAuth()
  const update = useUpdateMyPreferences()

  if (auth.data?.user.personScoped) return null

  return (
    <Card>
      <CardHeader title="Weergave" subtitle="Wat een meekijker van je scherm kan aflezen" />
      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">Bedragen vervagen op het dashboard</p>
          <p className="mt-0.5 text-sm text-ink-faint">
            Geldbedragen staan onscherp zodra je het dashboard opent. Ga met de muis over een bedrag om alleen dat te
            lezen, of klik erop om alles scherp te zetten. Dat blijft een kwartier staan zolang je bezig bent, ook na
            een herlaadbeurt. Uren en aantallen blijven altijd zichtbaar.
          </p>
        </div>
        <Toggle
          checked={auth.data?.preferences.blurAmounts ?? false}
          onChange={(value) => update.mutate({ blurAmounts: value })}
          label="Bedragen vervagen"
        />
      </div>
    </Card>
  )
}
